<template>
	<div class="s-layout" :style="colorObject">
    <div class="s-container">
      <HeaderV3
        :meta="meta"
        :page-title="moduleConfig.page_title"
        :page-image="moduleConfig.page_image"
        :page-kv-image="moduleConfig.page_kv_image"
        :header-section="moduleConfig.header_section"
      />

      <div class="page wizard-mode-layout">
        <div v-if="isSubmitting" class="wizard-mode-layout__loading-overlay">
          <div class="wizard-mode-layout__loading-spinner"></div>
          <div class="wizard-mode-layout__loading-text">載入中...</div>
        </div>
        <router-view />
      </div>
    </div>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import branchApi from '@/apis/liff/v2/branch'
import staffApi from '@/apis/liff/v2/staff'
import bookingApi from '@/apis/liff/v2/booking'
import HeaderV3 from '@/components/Page/Liff/Shared/HeaderV3'
import { themeColorFn } from "@/mixins/liff/themeColor";
import BookingPreset from '@/models/BookingPreset.model'
import BookingBusinessLogic from '@/utils/booking/BookingBusinessLogic'
import _ from "lodash";

const bookingBusinessLogic = new BookingBusinessLogic()

export default {
	mixins: [themeColorFn({ themeConfigPage: 'reservation' })],
	components: {
		HeaderV3
	},
	data() {
		return {
			isSubmitting: false
		}
	},
	provide() {
		return {
      back: () => this.back(),
      proceed: () => this.proceed(),
      hasPreviousStep: () => this.hasPreviousStep,
      textForProceedButton: () => this.textForProceedButton,
		}
	},
	computed: {
		...mapGetters({
			meta: "liffGeneral/meta",
      themeConfig: 'liffGeneral/themeConfig',
      getModule: 'liffModule/getModule',
      user: 'liffAuth/user',
    }),
    ...mapState('liffBookingWizard', ['bookingPreset', 'availableServices', 'selectedBranch', 'selectedStaff', 'selectedServiceIds', 'selectedSchedule', 'extraFormData']),
    ...mapGetters('liffBookingWizard', ['selectedServices']),
    moduleConfig() {
      return this.getModule('liff_reservation').module_config || {}
    },
    dataModel() {
      return new BookingPreset(this.bookingPreset)
    },
    hasPreviousStep() {
      return this.dataModel.previousWizardStep(this.$route.name)
    },
    isLastStepBeforeSubmitting() {
      return this.dataModel.nextWizardStep(this.$route.name) === 'LiffReservationSuccess'
    },
    textForProceedButton() {
      if (this.isLastStepBeforeSubmitting) {
        return this.dataModel.submitButtonText
      }

      return '下一步'
    },
	},
	async created() {
    await this.init();
  },
  methods: {
    ...mapActions('liffBookingWizard', ['fetchBookingPreset', 'fetchServices', 'validateBookingPreset', 'selectBranch', 'selectStaff', 'selectService', 'setSubmittedBooking']),
    async init () {
      const data = await this.fetchBookingPreset({
        bookingPresetId: this.$route.params.bookingPresetId,
        branchId: this.$route.query.branch_id
      })

      if (false === this.dataModel.isWizardMode) {
        this.$router.push({
          name: 'LiffBookingForm'
        })
      }

      if (this.dataModel.requireCustomerRegistration() && !this.user.registered_at) {
        return this.$router.push({ name: "LiffRegisterIndex" })
      }

      try {
        this.validateBookingPreset(data)
      } catch (error) {
        if (error.messages) {
          this.$swal("錯誤", error.messages.join("<br/>"), "warning");
        } else {
          console.error(error)
        }
      }

      await Promise.all([
        this.setSelectedBranch(),
        this.setSelectedStaff(),
        this.setSelectedService(),
      ])

      if (this.$route.name == 'LiffReservation') {
        this.$router.push({ name: this.dataModel.nextWizardStep('LiffReservation') })
      }
    },
    async setSelectedBranch () {
      if (this.$route.query.branch_id) {
        const branchResponse = await branchApi.getSingleBranch(this.$route.query.branch_id)
        this.selectBranch({ id: this.$route.query.branch_id, name: branchResponse.data.data.name })
      }
    },
    async setSelectedStaff () {
      if (this.$route.query.staff_id) {
        const staffResponse = await staffApi.getStaff(this.$route.query.staff_id)
        this.selectStaff({ id: this.$route.query.staff_id, name: staffResponse.name, img: staffResponse.avatar_url })
      }
    },
    async setSelectedService () {
      if (this.$route.query.service_id) {
        this.selectService(this.$route.query.service_id)
        if (this.availableServices.length === 0) {
          await this.fetchServices({
            bookingPreset: this.bookingPreset,
            branchId: this.$route.query.branch_id,
            staffId: this.$route.query.staff_id
          })
        }
      }
    },
    async back() {
      await this.$router.push({
        name: this.dataModel.previousWizardStep(this.$route.name),
        query: {
          branch_id: this.selectedBranch.id,
          staff_id: this.selectedStaff.id,
          service_id: this.selectedServiceIds,
        }
      });
    },
    async proceed() {
      if (this.isLastStepBeforeSubmitting) {
        return await this.submitBooking()
      }

      await this.$router.push({
        name: this.dataModel.nextWizardStep(this.$route.name),
        query: {
          branch_id: this.selectedBranch.id,
          staff_id: this.selectedStaff.id,
          service_id: this.selectedServiceIds,
        }
      });
    },
    async submitBooking() {
      this.isSubmitting = true
      try {
        const serviceDuration = _.sumBy(this.selectedServices, 'duration')

        const dataToSave = {
          preset_id: this.$route.params.bookingPresetId,
          fields: this.setDefaultFieldValue(this.extraFormData || {}),
          staff_id: this.selectedStaff.id,
          branch_id: this.selectedBranch.id,
          service_id: this.selectedServiceIds,
          start_at: this.selectedSchedule.start_at,
          end_at: bookingBusinessLogic.endDateTimeToSubmitInWizard(this.selectedSchedule.start_at, serviceDuration).format('YYYY-MM-DD HH:mm:ss')
        }

        const saveToCustomer = {}
        const allFields = _.get(this.bookingPreset, 'config.fields', [])
        allFields.forEach(field => {
          const fieldId = field._id
          const saveToPath = field.save_to_customer_data
          if (saveToPath && dataToSave.fields[fieldId]) {
            saveToCustomer[fieldId] = saveToPath
          }
        })

        if (Object.keys(saveToCustomer).length > 0) {
          dataToSave.save_to_customer = saveToCustomer
        }

        const res = await bookingApi.saveBooking(dataToSave)
        await this.setSubmittedBooking(res.data)

        await this.$router.push({
          name: 'LiffReservationSuccess'
        })
      } catch (e) {
        console.error(e)
        this.$swal.fire({ title: "送出失敗", type: "error" })
      } finally {
        this.isSubmitting = false
      }
    },
    setDefaultFieldValue(inputObject) {
      if (this.dataModel.startAtFieldId) {
        inputObject[this.dataModel.startAtFieldId] = this.selectedSchedule.start_at
      }

      if (this.dataModel.branchFieldId) {
        inputObject[this.dataModel.branchFieldId] = {
          branch_provider: this.dataModel.branchField?.branch_provider,
          text: this.selectedBranch.name,
          value: this.selectedBranch.id,
        }
      }

      if (this.dataModel.serviceFieldId) {
        const serviceField = this.dataModel.serviceField
        inputObject[serviceField?._id] = this.selectedServices.map((service) => {
          return {
            "text": service.title,
            "type": serviceField.type,
            "collection_id": service.collection_id,
            "collection_item_id": service.id
          }
        })
      }
      return inputObject
    },
  }
}
</script>

<style lang="scss" scoped>
@import "../../../assets/css/liff/main.css";
@import "../../../assets/css/liff/liff_init_theme_config.css";

.s-layout {
  color: var(--liff-primary_text_color);
  background-color: var(--liff-layout_bg_color);
  --s-gray: #979797 !important;
  --s-gray-light: #f0f0f0 !important;
  --s-danger: #fe0000;
}

.s-container {
  min-height: 100vh;
}

.page {
  padding: 16px 12px;
  background: var(--liff-content_bg_color);
}

::v-deep .wizard-mode-layout {
  position: relative;

  &__loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.8);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 100;
    border-radius: 10px;
  }

  &__loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid var(--s-primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 10px;
  }

  &__loading-text {
    font-size: 14px;
    color: var(--s-primary);
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  &__page-title {
    margin-bottom: 16px;
    font-size: 1.5rem;
    font-weight: 500;
    text-align: center;
  }

  &__card {
    border-radius: 10px;
    border: none;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);

    .card-header {
      border-radius: 10px 10px 0 0;
      border-bottom: 1px solid #E5E5EA;
      font-size: 16px;
      font-weight: 500;
      padding: 16px 16px 12px 16px;
    }
  }

  &__footer-navigation {
    position: fixed;
    max-width: 768px;
    margin: auto;
    background-color: #fff;
    bottom: 0;
    width: 100%;
    left: 0;
    right: 0;
    padding: 12px;
    box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.1);
    font-size: 13px;
    z-index: 20;
  }

  &__footer-button-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
}
</style>
